import React, { useState, useEffect } from 'react';
import {
  User,
  Mail,
  Phone,
  Building2,
  Shield,
  Key,
  Save,
  Camera,
  Loader2,
  Eye,
  EyeOff
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import { toast } from 'sonner';

const Profile = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    extension: '',
    department: '',
    role: '',
    timezone: 'America/New_York',
    language: 'en-US',
    avatar: null
  });

  const [security, setSecurity] = useState({
    twoFactorEnabled: false,
    emailNotifications: true,
    smsNotifications: false,
    loginAlerts: true
  });

  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Load user data from auth context
  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        extension: user.extension || user.id || '',
        department: user.department || '',
        role: user.role || 'User',
        timezone: 'America/New_York',
        language: 'en-US',
        avatar: null
      });
    }
  }, [user]);

  const handleSaveProfile = async () => {
    setSaving(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSaving(false);
    setSaved(true);
    toast.success('Profile saved successfully');
    setTimeout(() => setSaved(false), 2000);
  };

  const handleChangePassword = async () => {
    if (!passwords.currentPassword || !passwords.newPassword || !passwords.confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    
    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwords.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      const response = await authAPI.changePassword(
        profile.email,
        passwords.currentPassword,
        passwords.newPassword
      );
      
      if (response.data.success) {
        toast.success('Password changed successfully! Use your new password for both web login and SIP.');
        setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      }
    } catch (error) {
      console.error('Password change error:', error);
      const message = error.response?.data?.detail || 'Failed to change password';
      toast.error(message);
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">My Profile</h1>
          <p className="text-gray-400 mt-1">Manage your account settings and preferences</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <Card className="bg-[#12121a] border-[#1e1e2d] lg:col-span-1">
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center">
              <div className="relative mb-4">
                <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                  <span className="text-3xl font-bold text-white">
                    {profile.name.charAt(0)}
                  </span>
                </div>
                <button className="absolute bottom-0 right-0 p-2 bg-blue-600 rounded-full hover:bg-blue-700 transition-colors">
                  <Camera className="w-4 h-4 text-white" />
                </button>
              </div>
              <h2 className="text-xl font-semibold text-white">{profile.name}</h2>
              <p className="text-gray-400 text-sm">{profile.role}</p>
              <p className="text-gray-500 text-sm mt-1">Ext: {profile.extension}</p>
              
              <div className="w-full mt-6 space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-400">{profile.email}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Phone className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-400">{profile.phone}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Building2 className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-400">{profile.department}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Settings Tabs */}
        <Card className="bg-[#12121a] border-[#1e1e2d] lg:col-span-2">
          <Tabs defaultValue="general" className="w-full">
            <CardHeader className="border-b border-[#1e1e2d]">
              <TabsList className="bg-[#1e1e2d] border border-[#2a2a3d]">
                <TabsTrigger value="general" className="data-[state=active]:bg-blue-600">
                  <User className="w-4 h-4 mr-2" />
                  General
                </TabsTrigger>
                <TabsTrigger value="security" className="data-[state=active]:bg-blue-600">
                  <Shield className="w-4 h-4 mr-2" />
                  Security
                </TabsTrigger>
                <TabsTrigger value="password" className="data-[state=active]:bg-blue-600">
                  <Key className="w-4 h-4 mr-2" />
                  Password
                </TabsTrigger>
              </TabsList>
            </CardHeader>

            {/* General Tab */}
            <TabsContent value="general" className="m-0">
              <CardContent className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-gray-400">Full Name</Label>
                    <Input
                      value={profile.name}
                      onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                      className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-400">Email Address</Label>
                    <Input
                      type="email"
                      value={profile.email}
                      onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                      className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-400">Phone Number</Label>
                    <Input
                      value={profile.phone}
                      onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                      className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-400">Extension</Label>
                    <Input
                      value={profile.extension}
                      disabled
                      className="bg-[#1e1e2d] border-[#2a2a3d] text-white opacity-50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-400">Department</Label>
                    <Input
                      value={profile.department}
                      onChange={(e) => setProfile({ ...profile, department: e.target.value })}
                      className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-400">Role</Label>
                    <Input
                      value={profile.role}
                      disabled
                      className="bg-[#1e1e2d] border-[#2a2a3d] text-white opacity-50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-400">Timezone</Label>
                    <Select
                      value={profile.timezone}
                      onValueChange={(value) => setProfile({ ...profile, timezone: value })}
                    >
                      <SelectTrigger className="bg-[#1e1e2d] border-[#2a2a3d] text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                        <SelectItem value="America/New_York">America/New_York (EST)</SelectItem>
                        <SelectItem value="America/Chicago">America/Chicago (CST)</SelectItem>
                        <SelectItem value="America/Denver">America/Denver (MST)</SelectItem>
                        <SelectItem value="America/Los_Angeles">America/Los_Angeles (PST)</SelectItem>
                        <SelectItem value="Europe/London">Europe/London (GMT)</SelectItem>
                        <SelectItem value="Europe/Paris">Europe/Paris (CET)</SelectItem>
                        <SelectItem value="Asia/Tokyo">Asia/Tokyo (JST)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-400">Language</Label>
                    <Select
                      value={profile.language}
                      onValueChange={(value) => setProfile({ ...profile, language: value })}
                    >
                      <SelectTrigger className="bg-[#1e1e2d] border-[#2a2a3d] text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                        <SelectItem value="en-US">English (US)</SelectItem>
                        <SelectItem value="en-GB">English (UK)</SelectItem>
                        <SelectItem value="es">Spanish</SelectItem>
                        <SelectItem value="fr">French</SelectItem>
                        <SelectItem value="de">German</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={handleSaveProfile}
                    disabled={saving}
                    className={saved ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}
                  >
                    {saving ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                    ) : saved ? (
                      'Saved!'
                    ) : (
                      <><Save className="w-4 h-4 mr-2" /> Save Changes</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </TabsContent>

            {/* Security Tab */}
            <TabsContent value="security" className="m-0">
              <CardContent className="p-6 space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-[#1e1e2d] rounded-lg">
                    <div>
                      <p className="text-white font-medium">Two-Factor Authentication</p>
                      <p className="text-sm text-gray-400">Add an extra layer of security to your account</p>
                    </div>
                    <Switch
                      checked={security.twoFactorEnabled}
                      onCheckedChange={(checked) => setSecurity({ ...security, twoFactorEnabled: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-[#1e1e2d] rounded-lg">
                    <div>
                      <p className="text-white font-medium">Email Notifications</p>
                      <p className="text-sm text-gray-400">Receive email alerts for important events</p>
                    </div>
                    <Switch
                      checked={security.emailNotifications}
                      onCheckedChange={(checked) => setSecurity({ ...security, emailNotifications: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-[#1e1e2d] rounded-lg">
                    <div>
                      <p className="text-white font-medium">SMS Notifications</p>
                      <p className="text-sm text-gray-400">Receive SMS alerts for critical events</p>
                    </div>
                    <Switch
                      checked={security.smsNotifications}
                      onCheckedChange={(checked) => setSecurity({ ...security, smsNotifications: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-[#1e1e2d] rounded-lg">
                    <div>
                      <p className="text-white font-medium">Login Alerts</p>
                      <p className="text-sm text-gray-400">Get notified when someone logs into your account</p>
                    </div>
                    <Switch
                      checked={security.loginAlerts}
                      onCheckedChange={(checked) => setSecurity({ ...security, loginAlerts: checked })}
                    />
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={handleSaveProfile}
                    disabled={saving}
                    className={saved ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}
                  >
                    {saving ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                    ) : saved ? (
                      'Saved!'
                    ) : (
                      <><Save className="w-4 h-4 mr-2" /> Save Changes</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </TabsContent>

            {/* Password Tab */}
            <TabsContent value="password" className="m-0">
              <CardContent className="p-6 space-y-6">
                <div className="space-y-4 max-w-md">
                  <div className="space-y-2">
                    <Label className="text-gray-400">Current Password</Label>
                    <div className="relative">
                      <Input
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={passwords.currentPassword}
                        onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
                        className="bg-[#1e1e2d] border-[#2a2a3d] text-white pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                      >
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-400">New Password</Label>
                    <div className="relative">
                      <Input
                        type={showNewPassword ? 'text' : 'password'}
                        value={passwords.newPassword}
                        onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                        className="bg-[#1e1e2d] border-[#2a2a3d] text-white pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-400">Confirm New Password</Label>
                    <Input
                      type="password"
                      value={passwords.confirmPassword}
                      onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                      className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                    />
                  </div>

                  <div className="pt-4">
                    <p className="text-sm text-gray-400 mb-2">Password requirements:</p>
                    <ul className="text-sm text-gray-500 space-y-1 list-disc list-inside">
                      <li>Minimum 8 characters</li>
                      <li>At least one uppercase letter</li>
                      <li>At least one number</li>
                      <li>At least one special character</li>
                    </ul>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={handleChangePassword}
                    disabled={changingPassword || !passwords.currentPassword || !passwords.newPassword || !passwords.confirmPassword}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {changingPassword ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Changing...</>
                    ) : (
                      <><Key className="w-4 h-4 mr-2" /> Change Password</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
};

export default Profile;
